import {
  ChevronDown,
  ChevronUp,
  Disc3,
  FastForward,
  Pause,
  Play,
  Rewind,
  X,
} from 'lucide-react';
import { useCallback, useRef } from 'react';

import type { SpectrumParams } from '../../audio/spectrum';
import type { Player } from '../../hooks/usePlayer';
import { trackPath } from '../../hooks/useRoute';
import { formatDuration } from '../format';
import { SpectrumStrip } from './SpectrumStrip';
import { VolumeSlider } from './VolumeSlider';

type PlayerDockProps = {
  player: Player;
  spectrumParams: SpectrumParams;
  analyserOpen: boolean;
  onAnalyserOpen(open: boolean): void;
  onNavigate(path: string): void;
};

/**
 * The persistent bottom dock.
 *
 * The live spectrum analyser runs full-bleed across the bottom of the screen, over a
 * Spotify-shaped bar: identity left, transport centred over its progress bar, volume
 * right.
 *
 * Clicking the analyser collapses it to a thin reveal strip. That is a real teardown
 * rather than a hidden element: the canvas unmounts, so its requestAnimationFrame loop
 * stops instead of painting frames nobody can see.
 *
 * The dock deliberately carries no waveform. It and the progress bar were both showing
 * position, and the bar does that job more precisely in far less height. The waveform
 * lives on the track page, where it has room to be worth reading.
 *
 * There are no next/previous buttons on purpose. This hosts long sets, not a track
 * queue; moving between them is a library action, not a transport one.
 */
export function PlayerDock({
  player,
  spectrumParams,
  analyserOpen,
  onAnalyserOpen,
  onNavigate,
}: PlayerDockProps) {
  const { track } = player;
  const scrubRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const fractionFrom = useCallback((clientX: number) => {
    const rect = scrubRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
  }, []);

  if (!track) return null;

  const duration = player.duration || track.durationSeconds || 0;
  const progress = duration > 0 ? Math.min(player.time / duration, 1) : 0;

  return (
    <aside className={`dock${analyserOpen ? '' : ' is-collapsed'}`} aria-label="Player">
      {analyserOpen ? (
        <div className="dock-visuals">
          <button
            type="button"
            className="spectrum-collapse"
            onClick={() => onAnalyserOpen(false)}
            aria-expanded
            aria-label="Hide the spectrum analyser"
            title="Hide the analyser"
          >
            <SpectrumStrip
              engine={player.engine}
              params={spectrumParams}
              playing={player.playing}
              compact
            />
            <span className="spectrum-hint">
              <ChevronDown size={14} />
            </span>
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="spectrum-reveal"
          onClick={() => onAnalyserOpen(true)}
          aria-expanded={false}
          aria-label="Show the spectrum analyser"
          title="Show the analyser"
        >
          <ChevronUp size={14} />
        </button>
      )}

      <div className="dock-bar">
        <div className="dock-identity">
          <div className="dock-cover">
            {track.coverUrl ? <img src={track.coverUrl} alt="" /> : <Disc3 size={18} />}
          </div>
          <div className="dock-meta">
            <a
              href={trackPath(track.id)}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(trackPath(track.id));
              }}
            >
              {track.title}
            </a>
            <span>{track.artist || 'Unknown artist'}</span>
          </div>
        </div>

        <div className="dock-center">
          <div className="dock-controls">
            <button
              type="button"
              className="transport-seek"
              onClick={() => player.skip(-player.skipSeconds)}
              aria-label={`Rewind ${player.skipSeconds} seconds`}
              title={`Rewind ${player.skipSeconds}s (left arrow)`}
            >
              <Rewind size={19} fill="currentColor" />
            </button>
            <button
              type="button"
              className="transport-play"
              onClick={player.toggle}
              disabled={player.loading}
              aria-label={player.playing ? 'Pause' : 'Play'}
            >
              {player.playing ? (
                <Pause size={19} fill="currentColor" />
              ) : (
                <Play size={19} fill="currentColor" />
              )}
            </button>
            <button
              type="button"
              className="transport-seek"
              onClick={() => player.skip(player.skipSeconds)}
              aria-label={`Fast forward ${player.skipSeconds} seconds`}
              title={`Forward ${player.skipSeconds}s (right arrow)`}
            >
              <FastForward size={19} fill="currentColor" />
            </button>
          </div>

          <div className="dock-seek">
            <span className="numeric">{formatDuration(player.time)}</span>
            <div
              ref={scrubRef}
              className="scrubber"
              role="slider"
              tabIndex={0}
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={Math.round(duration)}
              aria-valuenow={Math.round(player.time)}
              aria-valuetext={formatDuration(player.time)}
              onPointerDown={(event) => {
                draggingRef.current = true;
                event.currentTarget.setPointerCapture(event.pointerId);
                player.seekToFraction(fractionFrom(event.clientX));
              }}
              onPointerMove={(event) => {
                if (draggingRef.current) player.seekToFraction(fractionFrom(event.clientX));
              }}
              onPointerUp={(event) => {
                draggingRef.current = false;
                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                  event.currentTarget.releasePointerCapture(event.pointerId);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') {
                  event.preventDefault();
                  player.skip(-player.skipSeconds);
                } else if (event.key === 'ArrowRight') {
                  event.preventDefault();
                  player.skip(player.skipSeconds);
                }
              }}
            >
              <div className="scrubber-track" />
              <div className="scrubber-fill" style={{ width: `${progress * 100}%` }} />
              <div className="scrubber-handle" style={{ left: `${progress * 100}%` }} />
            </div>
            <span className="numeric">{formatDuration(duration)}</span>
          </div>
        </div>

        <div className="dock-side">
          <VolumeSlider
            volume={player.volume}
            muted={player.muted}
            onChange={player.setVolume}
            onToggleMute={player.toggleMute}
          />
          <button
            type="button"
            className="icon-button"
            onClick={player.close}
            aria-label="Close player"
          >
            <X size={17} />
          </button>
        </div>
      </div>
    </aside>
  );
}
